import React, { useCallback, useEffect } from "react";
import Select, { MultiValue } from "react-select";
import { DropdownIndicator, REACT_SELECT_STYLES } from "../style-constants";

export type AgeRange =
  | "under-6-months"
  | "6-months-to-1-year"
  | "1-to-7-years"
  | "over-7-years";
export type AgeOption = { value: AgeRange; label: string };

const ageOptions: ReadonlyArray<AgeOption> = [
  { value: "under-6-months", label: "Under 6 mo." },
  { value: "6-months-to-1-year", label: "6 mo. - 1 yr" },
  { value: "1-to-7-years", label: "1-7 yrs" },
  { value: "over-7-years", label: "7+ yrs" },
] as const;

const OPTION_MAP: Record<AgeRange, AgeOption> = {
  "under-6-months": ageOptions[0],
  "6-months-to-1-year": ageOptions[1],
  "1-to-7-years": ageOptions[2],
  "over-7-years": ageOptions[3],
};

const AgeSelect: React.FC<{
  value: MultiValue<AgeOption>;
  onChange: (newValue: MultiValue<AgeOption>) => void;
}> = ({ value, onChange }) => {
  // On mount or when URL changes (if you want to handle browser back/forward),
  // we parse the current URL and update `onChange` with the selected values.
  const readStateFromUrl = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const ageParam = params.get("age");
    if (ageParam) {
      const ageValues = ageParam
        .split(",")
        .map((age) => OPTION_MAP[age as AgeRange])
        .filter(Boolean);
      onChange(ageValues);
    } else {
      // If no param, ensure value is empty
      if (value.length > 0) {
        onChange([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    readStateFromUrl();

    // Listen for URL changes
    window.addEventListener("popstate", readStateFromUrl);
    return () => {
      window.removeEventListener("popstate", readStateFromUrl);
    };
  }, [readStateFromUrl]);

  // Whenever `value` changes, update the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (value.length > 0) {
      params.set("age", value.map((v) => v.value).join(","));
    } else {
      params.delete("age");
    }
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    // Update the URL without reloading
    window.history.pushState({}, "", newUrl);
  }, [value]);

  return (
    <Select
      styles={REACT_SELECT_STYLES}
      menuPortalTarget={document.body}
      isMulti={true}
      options={ageOptions}
      value={value}
      onChange={onChange}
      components={{ DropdownIndicator }}
      placeholder="All ages"
      aria-label="Age range filter"
    />
  );
};

export default AgeSelect;
